/**
 * API client.
 *
 * Much thinner than Vendor OS's, and deliberately so — same-origin removes the
 * three things that made that one complicated:
 *
 *   - No CORS dance. Plain JSON, real headers, real status codes.
 *   - No token to carry. The session is an httpOnly cookie the browser sends
 *     for us, which also means page JavaScript cannot leak it.
 *   - No "did the backend return its health payload instead of data?" guard.
 *     That existed because a slow Apps Script POST can be served as a GET; there
 *     is no such redirect here.
 *
 * What we keep from it, because it was learned the hard way: reads retry and
 * writes do not, and a write whose reply never arrived says so honestly rather
 * than reporting a failure that may not have happened.
 */

const RETRYABLE = new Set(['GET', 'HEAD']);
const MAX_ATTEMPTS = 3;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export class ApiError extends Error {
  constructor(message, { status = 0, kind = 'internal' } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.kind = kind;
  }
  /** Only a genuine session failure should bounce the user to sign-in. */
  get isAuth() {
    return this.kind === 'auth';
  }
}

let onSessionLost = null;
export const setSessionLostHandler = (fn) => {
  onSessionLost = fn;
};

async function once(method, path, body, signal) {
  let res;
  try {
    res = await fetch(`/api${path}`, {
      method,
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    throw new ApiError(`Network error: ${err.message}`, { kind: 'transport' });
  }

  let json = null;
  try {
    json = await res.json();
  } catch {
    if (!res.ok) throw new ApiError(`HTTP ${res.status}`, { status: res.status, kind: 'transport' });
    throw new ApiError('The server sent a response we could not read.', { kind: 'transport' });
  }

  if (json && json.error) {
    const err = new ApiError(json.error, { status: res.status, kind: json.kind || 'internal' });
    if (err.isAuth && onSessionLost) onSessionLost(err);
    throw err;
  }
  if (!res.ok) throw new ApiError(`HTTP ${res.status}`, { status: res.status, kind: 'transport' });
  return json.data;
}

async function request(method, path, body, opts = {}) {
  const attempts = RETRYABLE.has(method) ? MAX_ATTEMPTS : 1;
  let last;
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      return await once(method, path, body, opts.signal);
    } catch (err) {
      last = err;
      if (err?.name === 'AbortError') throw err;
      if (err.kind !== 'transport' || attempt === attempts) break;
      await sleep(300 * attempt);
    }
  }
  if (last?.kind === 'transport' && !RETRYABLE.has(method)) {
    throw new ApiError(
      `${last.message}. The change may still have gone through — refresh before trying again.`,
      { kind: 'transport' },
    );
  }
  throw last;
}

const qs = (params) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params || {})) {
    if (v !== undefined && v !== null && v !== '') p.set(k, v);
  }
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const api = {
  health: () => request('GET', '/health'),
  bootstrap: () => request('GET', '/bootstrap'),

  signInWithGoogle: (credential) => request('POST', '/auth/google', { credential }),
  requestMagicLink: (email) => request('POST', '/auth/magic-link', { email }),
  signOut: (everywhere = false) =>
    request('POST', `/auth/signout${everywhere ? '?all=1' : ''}`, {}),

  savePrefs: (prefs) => request('PATCH', '/me/prefs', prefs),

  today: () => request('GET', '/today'),

  companies: (params) => request('GET', `/companies${qs(params)}`),
  company: (id) => request('GET', `/companies/${id}`),
  createCompany: (body, confirm) =>
    request('POST', `/companies${confirm ? '?confirm=1' : ''}`, body),
  updateCompany: (id, body) => request('PATCH', `/companies/${id}`, body),
  assign: (id, body) => request('POST', `/companies/${id}/assign`, body),

  contacts: (params) => request('GET', `/contacts${qs(params)}`),
  createContact: (body) => request('POST', '/contacts', body),
  updateContact: (id, body) => request('PATCH', `/contacts/${id}`, body),

  board: (params) => request('GET', `/deals/board${qs(params)}`),
  deals: (params) => request('GET', `/deals${qs(params)}`),
  deal: (id) => request('GET', `/deals/${id}`),
  createDeal: (body) => request('POST', '/deals', body),
  updateDeal: (id, body) => request('PATCH', `/deals/${id}`, body),
  moveStage: (id, body) => request('POST', `/deals/${id}/stage`, body),

  activities: (params) => request('GET', `/activities${qs(params)}`),
  logActivity: (body) => request('POST', '/activities', body),

  tasks: (params) => request('GET', `/tasks${qs(params)}`),
  createTask: (body) => request('POST', '/tasks', body),
  completeTask: (id, done = true) => request('POST', `/tasks/${id}/complete`, { done }),

  // --- the funnel (B2) ---
  prospects: (params) => request('GET', `/prospects${qs(params)}`),
  importProspects: (body) => request('POST', '/prospects/import', body),
  queueProspect: (id, body) => request('POST', `/prospects/${id}/queue`, body || {}),
  dismissProspect: (id, reason) => request('POST', `/prospects/${id}/dismiss`, { reason }),
  convertProspect: (id, body) => request('POST', `/prospects/${id}/convert`, body || {}),
  reviveProspect: (body) => request('POST', '/prospects/revive', body),

  calls: (params) => request('GET', `/calls${qs(params)}`),
  logCall: (body) => request('POST', '/calls', body),

  briefs: (params) => request('GET', `/briefs${qs(params)}`),
  brief: (id) => request('GET', `/briefs/${id}`),
  createBrief: (body) => request('POST', '/briefs', body),
  createDealFromBrief: (id, body) => request('POST', `/briefs/${id}/create-deal`, body || {}),

  graduate: (dealId, body) => request('POST', `/deals/${dealId}/graduate`, body),
  updateGraduation: (dealId, body) => request('PATCH', `/deals/${dealId}/graduation`, body),

  accounts: (params) => request('GET', `/accounts${qs(params)}`),
  logService: (id, body) => request('POST', `/accounts/${id}/service`, body),

  users: (params) => request('GET', `/users${qs(params)}`),

  // --- management (B3) ---
  review: (period) => request('GET', `/review/${period}`),
  saveReviewNote: (period, body) => request('POST', `/review/${period}/note`, body),
  publishReview: (period, body) => request('POST', `/review/${period}/publish`, body || {}),
  unpublishReview: (period) => request('POST', `/review/${period}/unpublish`, {}),

  insights: (params) => request('GET', `/insights${qs(params)}`),

  adminUsers: () => request('GET', '/admin/users'),
  createUser: (body) => request('POST', '/admin/users', body),
  updateUser: (id, body) => request('PATCH', `/admin/users/${id}`, body),
  deleteUser: (id) => request('DELETE', `/admin/users/${id}`),

  adminConfig: () => request('GET', '/admin/config'),
  updateConfig: (key, value) => request('PATCH', `/admin/config/${key}`, { value }),

  connections: () => request('GET', '/admin/connections'),
  updateConnection: (provider, body) => request('PATCH', `/admin/connections/${provider}`, body),

  quotas: (period) => request('GET', `/admin/quotas${qs({ period })}`),
  saveQuotas: (period, rows) => request('POST', '/admin/quotas', { period, rows }),

  targets: (year) => request('GET', `/admin/targets${qs({ year })}`),
  saveTarget: (body) => request('POST', '/admin/targets', body),

  rebuildRollups: (period) => request('POST', '/admin/rollups/rebuild', { period }),
  auditLog: (limit) => request('GET', `/admin/audit${qs({ limit })}`),

  // --- B4: notifications and scheduled work ---
  notifications: (params) => request('GET', `/notifications${qs(params)}`),
  markNotificationsRead: (ids) => request('POST', '/notifications/read', ids ? { ids } : {}),

  jobs: () => request('GET', '/jobs'),
  jobHistory: (name) => request('GET', `/jobs/${name}`),
  runJobNow: (name) => request('POST', `/jobs/${name}`, {}),

  // The CSV itself is a browser navigation, not a fetch — the response is an
  // attachment and letting the browser handle it keeps the download in the
  // download manager instead of in memory. This lists what is available.
  exports: () => request('GET', '/exports'),
  exportHref: (name) => `/api/exports/${name}`,
};

export { request };
